import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  InputAdornment,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TransportationOption } from '@/types';
import { useI18n } from '@/hooks/useI18n';

const transportationOptionSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['individual', 'group', 'public']),
  pricePerPerson: z.number().min(0, 'Price must be non-negative')
});

type TransportationOptionFormData = z.infer<typeof transportationOptionSchema>;

interface AddTransportationOptionFormProps {
  onAddTransportationOption: (option: Omit<TransportationOption, 'id'>) => void;
  trigger: React.ReactNode;
}

const AddTransportationOptionForm: React.FC<AddTransportationOptionFormProps> = ({
  onAddTransportationOption,
  trigger
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<TransportationOptionFormData>({
    resolver: zodResolver(transportationOptionSchema),
    defaultValues: {
      name: '',
      type: 'individual',
      pricePerPerson: 0
    }
  });

  const onSubmit = (data: TransportationOptionFormData) => {
    onAddTransportationOption(data);
    reset();
    setOpen(false);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  return (
    <>
      <Box onClick={() => setOpen(true)} sx={{ cursor: 'pointer' }}>
        {trigger}
      </Box>
      
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="h6" component="div">
            {t('transportation.form.addTitle')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: 'grey.500',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('transportation.form.name')}
                    placeholder={t('transportation.form.namePlaceholder')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    fullWidth
                    required
                  />
                )}
              />

              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel>{t('transportation.form.type')}</InputLabel>
                    <Select
                      {...field}
                      label={t('transportation.form.type')}
                    >
                      <MenuItem value="individual">
                        🚗 {t('transportation.types.individual')}
                      </MenuItem>
                      <MenuItem value="group">
                        🚌 {t('transportation.types.group')}
                      </MenuItem>
                      <MenuItem value="public">
                        🚆 {t('transportation.types.public')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="pricePerPerson"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={t('transportation.form.pricePerPerson')}
                    placeholder={t('transportation.form.pricePerPersonPlaceholder')}
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                    error={!!errors.pricePerPerson}
                    helperText={errors.pricePerPerson?.message}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleClose} 
            variant="outlined"
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit(onSubmit)} 
            variant="contained"
          >
            {t('transportation.form.addTransportationOption')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddTransportationOptionForm;